import { Notice } from '@p1plug/ui';
import { C, Code, DocPage, H2, P, Snippet } from '../doc';
import { errorBody, errorHandling } from '../samples';

interface ErrorDoc {
  status: number;
  code: string;
  meaning: string;
  retry: string;
  fix: string;
}

/** The codes the API actually returns (internal/backend/api). Keep in step with it. */
const ERRORS: ErrorDoc[] = [
  {
    status: 400,
    code: 'bad_request',
    meaning:
      'The request could not be read: malformed JSON, an unknown field, or a query parameter out of range. The message names the field.',
    retry: 'No',
    fix: 'Correct the request. Unknown JSON fields are rejected on purpose, so a typo never passes silently.',
  },
  {
    status: 400,
    code: 'invalid_cursor',
    meaning: 'The cursor is malformed, was altered, or was issued to another organisation.',
    retry: 'No',
    fix: 'Use the next_cursor exactly as returned. To start over, call without after.',
  },
  {
    status: 401,
    code: 'unauthorized',
    meaning: 'The credential is missing, unknown, revoked, or the access token has expired.',
    retry: 'After getting a new token',
    fix: 'Check the Authorization header. With OAuth, request a new token; with an API key, check it has not been rotated.',
  },
  {
    status: 403,
    code: 'insufficient_scope',
    meaning: 'The credential is valid but does not carry the scope this operation needs.',
    retry: 'No',
    fix: 'Request a token with the scope, or have it added to the machine client. Each operation lists its scope in the reference.',
  },
  {
    status: 404,
    code: 'not_found',
    meaning:
      'The resource does not exist, or belongs to another organisation. The two are deliberately indistinguishable.',
    retry: 'No',
    fix: 'Check the identifier and that you are calling the environment it was created in.',
  },
  {
    status: 404,
    code: 'no_telemetry',
    meaning: 'The meter point exists but no reading has arrived yet.',
    retry: 'Later',
    fix: 'Normal before activation. Wait for installation.activated rather than polling.',
  },
  {
    status: 409,
    code: 'invalid_transition',
    meaning: 'The order cannot move to that status from where it is, for example cancelling an activated order.',
    retry: 'No',
    fix: 'Read the order; the message names the current status.',
  },
  {
    status: 409,
    code: 'grant_not_active',
    meaning: 'The access grant was already revoked or has expired.',
    retry: 'No',
    fix: 'Nothing to do: the consent is already withdrawn.',
  },
  {
    status: 422,
    code: 'validation_failed',
    meaning: 'The request was understood but a value is missing or not acceptable. The message names it.',
    retry: 'No',
    fix: 'Correct the value. For orders: external_order_id, customer.name and property.address are required.',
  },
  {
    status: 422,
    code: 'idempotency_mismatch',
    meaning: 'The Idempotency-Key was used before with a different request body.',
    retry: 'No',
    fix: 'Use one key per distinct request. Reuse a key only to repeat the identical request.',
  },
  {
    status: 429,
    code: 'rate_limited',
    meaning:
      'Too many requests in a short time. The API does not rate-limit customers today; the code is reserved so your client can be ready for it.',
    retry: 'Yes, with backoff',
    fix: 'Slow down; spread bulk work over time.',
  },
  {
    status: 500,
    code: 'internal',
    meaning: 'P1Plug failed to process a valid request.',
    retry: 'Yes, with backoff',
    fix: 'Nothing on your side. Order creation is idempotent, so a retry cannot create a duplicate.',
  },
];

export function Errors() {
  return (
    <DocPage
      title="Errors"
      lead="An error tells you what failed, whether trying again can help, and what to change. This page lists every code the API returns."
    >
      <H2>The error format</H2>
      <P>
        Every error response has the same body. Branch on <C>error.code</C>, which is stable; show or log{' '}
        <C>error.message</C>, which is written for a developer and may be reworded.
      </P>
      <Snippet title="Error response" code={errorBody} />

      <H2>Retry or fix?</H2>
      <P>
        One rule covers almost everything: a timeout, a network failure, <C>429</C> and any <C>5xx</C> are worth
        retrying with backoff; any other <C>4xx</C> will fail the same way until you change something.
      </P>
      <Code samples={errorHandling} />

      <H2>Every error code</H2>
      <div className="dev-errors">
        {ERRORS.map((e) => (
          <section key={e.code} id={e.code} className="dev-error">
            <h3>
              <span className="p1-num dev-error-status">{e.status}</span> <code>{e.code}</code>
            </h3>
            <dl>
              <div>
                <dt>What happened</dt>
                <dd>{e.meaning}</dd>
              </div>
              <div>
                <dt>Retry?</dt>
                <dd>{e.retry}</dd>
              </div>
              <div>
                <dt>How to fix it</dt>
                <dd>{e.fix}</dd>
              </div>
            </dl>
          </section>
        ))}
      </div>

      <H2>Token errors</H2>
      <P>
        <C>POST /oauth/token</C> follows the OAuth standard instead:{' '}
        <C>{'{ "error": "invalid_client", "error_description": "…" }'}</C>. <C>invalid_client</C> (401) means an unknown
        client, a wrong secret or a disabled client; <C>invalid_scope</C> (400) means you asked for a scope the client
        does not have.
      </P>

      <Notice tone="info" title="Request identifiers are coming">
        Error responses do not yet carry a request ID to quote to support. Until they do, include the time of the
        request, the method and path, and your order reference when you contact P1Plug.
      </Notice>
    </DocPage>
  );
}
